import React, { useCallback } from 'react';
import type { LayoutChangeEvent, ViewProps } from 'react-native';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import type { PinData } from '@sweetgreen/garnish';
import {
  BodyText,
  Icon,
  LocationCard,
  theme,
  useBreakpointHelpers,
  useResponsive,
  VStack,
} from '@sweetgreen/garnish';

import { TooltipWithBanner } from '@order/components';
import { useLocalizationContext } from '@order/Localization';

import { useReorderState } from '../../../ReorderingScreen/hooks';
import type { LocationResultData } from '../../GraphQL';
import type { LocationResultsProps } from '../../LocationScreen.types';
import { getLocationA11yProps } from '../../LocationScreen.utils';

// ────────────────────────────────────────────────────────────────────────────────

export const LocationResults = ({
  locationSearchType,
  locationsForSelectedSearchType,
  locationsForOppositeSearchType,
  onCardPress,
  onCardFocus,
  onInfoPress,
  storeLocationCardHeight,
}: LocationResultsProps): React.ReactElement => {
  const { t } = useLocalizationContext();
  const { reordering, restaurantSlug } = useReorderState();
  const showImage = useLocationImageVisibility();
  const hasSelectedOutpost = locationSearchType === 'outpost';

  const renderLocation = useCallback(
    (location: PinData & LocationResultData, index: number) => {
      return (
        <LocationCardWrapper
          key={location.id}
          id={location.id}
          isOutpost={index !== 0 && location.isOutpost}
          onLayout={storeLocationCardHeight}
        >
          <LocationCard
            {...location}
            testID={`locationsearch.${location.restaurantSlug}`}
            isLoading={
              reordering
                ? restaurantSlug === location.restaurantSlug
                : undefined
            }
            onCardPress={onCardPress}
            onInfoPress={onInfoPress}
            onCardFocus={onCardFocus}
            imageUrl={showImage ? location.imageUrl : undefined}
            {...getLocationA11yProps(t, location, 'button')}
          />
        </LocationCardWrapper>
      );
    },
    [
      reordering,
      restaurantSlug,
      showImage,
      onCardFocus,
      onCardPress,
      onInfoPress,
      storeLocationCardHeight,
      t,
    ],
  );

  return (
    <VStack customSpacing={0}>
      <LocationResultsByType
        hasTitle={false}
        hasDivider={hasSelectedOutpost}
        locations={locationsForSelectedSearchType}
        locationSearchType={locationSearchType}
        renderLocation={renderLocation}
      />
      <LocationResultsByType
        hasTitle={locationsForOppositeSearchType.length > 0}
        hasDivider={!hasSelectedOutpost}
        locations={locationsForOppositeSearchType}
        locationSearchType={locationSearchType}
        renderLocation={renderLocation}
      />
    </VStack>
  );
};

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const LocationResultsByType = ({
  hasTitle,
  hasDivider,
  locationSearchType,
  locations,
  renderLocation,
}: LocationResultsByTypeProps) => {
  return (
    <>
      {hasTitle ? (
        <LocationByTypeTitle locationSearchType={locationSearchType} />
      ) : null}
      <VStack
        divider={hasDivider}
        dividerColor={theme.colors.LIGHT}
        customSpacing={0}
      >
        {locations.map(renderLocation)}
      </VStack>
    </>
  );
};

const LocationCardWrapper: React.FC<LocationCardWrapperProps> = (props) => {
  const { id, isOutpost, children, onLayout } = props;

  const { match } = useResponsive();

  // ─── HELPERS ─────────────────────────────────────────────────────

  const handleOnCardLayout = useCallback(
    (event: LayoutChangeEvent) => {
      onLayout?.(event, id);
    },
    [id, onLayout],
  );

  // ────────────────────────────────────────────────────────────────────────────────

  return (
    <View
      style={match([
        isOutpost ? styles.cardWrapperOutpostXS : styles.cardWrapperXS,
        isOutpost ? styles.cardWrapperOutpostSM : styles.cardWrapperSM,
      ])}
      onLayout={handleOnCardLayout}
    >
      {children}
    </View>
  );
};

const LocationByTypeTitle = ({
  locationSearchType,
}: Pick<LocationResultsProps, 'locationSearchType'>) => {
  const { t } = useLocalizationContext();

  return (
    <View style={styles.locationByTypeHeader}>
      <BodyText size={3} style={styles.locationByTypeHeaderLabel}>
        {t('location.result.divider', {
          invertedOrderType: locationSearchType,
        })}
      </BodyText>
      {locationSearchType === 'pickup' ? (
        <TooltipWithBanner label={t('location.result.outpost-hint')}>
          <View style={styles.locationByTypeHeaderIcon}>
            <Icon name="IconHelp" />
          </View>
        </TooltipWithBanner>
      ) : null}
    </View>
  );
};

const IMAGE_VISIBILITY_THRESHOLD = 700; // pixels of height.

// Should not show location image on smaller mobile screens.
const useLocationImageVisibility = () => {
  const { isMobile } = useBreakpointHelpers();
  const { height } = useWindowDimensions();

  return !isMobile || height > IMAGE_VISIBILITY_THRESHOLD;
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  cardWrapperXS: {
    paddingBottom: theme.spacing.medium,
  },
  cardWrapperSM: {
    paddingBottom: theme.spacing.large,
  },
  cardWrapperOutpostXS: {
    paddingBottom: theme.spacing.medium,
    paddingTop: theme.spacing.medium,
  },
  cardWrapperOutpostSM: {
    paddingBottom: theme.spacing.large,
    paddingTop: theme.spacing.large,
  },
  locationByTypeHeader: {
    flexDirection: 'row',
    marginTop: theme.spacing.medium,
    marginBottom: theme.spacing.large,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.NEUTRAL_4,
  },
  locationByTypeHeaderLabel: {
    marginBottom: theme.spacing.small,
  },
  locationByTypeHeaderIcon: {
    marginLeft: theme.spacing.small,
    marginBottom: theme.spacing.small,
    marginTop: -theme.spacing.xsmall / 2,
  },
});

// ────────────────────────────────────────────────────────────────────────────────

type LocationCardWrapperProps = {
  readonly id: string;
  readonly isOutpost: boolean;
  readonly onLayout?: (event: LayoutChangeEvent, id: string) => void;
} & Omit<ViewProps, 'onLayout'>;

type LocationResultsByTypeProps = Pick<
  LocationResultsProps,
  'locations' | 'locationSearchType'
> & {
  readonly renderLocation: (
    location: PinData & LocationResultData,
    index: number,
  ) => ViewProps['children'];
  readonly hasTitle: boolean;
  readonly hasDivider: boolean;
};
